import { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import store from 'client/store/store';
import { clearDrawStatus, selectBufferRadiusMetres } from 'client/store/drawSlice/drawSlice';
import { addMapMode, removeMapMode } from 'client/store/mapSlice/mapSlice';
import { setAlertOpen } from 'client/store/alertSlice/alertSlice';

import drawStyle, { resetDrawStyleCache } from 'client/pages/MapPage/layers/styles/drawStyle';
import { modifyStyle } from 'client/pages/MapPage/layers/styles';

import { Draw, Interactions, MapContext, Modify } from 'os-elements';
import { drawStates, mapModes } from 'client/utils/constants/appStates';
import { BOX, LINE } from 'client/utils/constants/symbolName';
import { useMaxDrawExtent, useLineDrawDimensions } from 'client/utils/auth/userPermissions';

const { DEFAULT, DRAWING, MODIFYING } = mapModes;
const { INVALID, EXCEEDS_EXTENT, OUTSIDE_SERVICE_AREA } = drawStates;

const DrawAndModify = ({ mapMode, drawType, addMapMode, removeMapMode, setAlertOpen, clearDrawStatus }) => {
    const maxDrawExtent = useMaxDrawExtent();
    const lineDrawDimensions = useLineDrawDimensions();
    const { map } = useContext(MapContext);
    const [drawSource, setDrawSource] = useState(null);
    const [aoiSource, setAoiSource] = useState(null);
    const bufferRadiusMetres = useSelector(selectBufferRadiusMetres);
    const bufferInitialised = useRef(false);

    useEffect(() => {
        setDrawSource(map?.getLayerById('draw')?.getSource());
        setAoiSource(map?.getLayerById('aoi')?.getSource());
    }, []);

    useEffect(() => {
        // New draw type implies a new session; reset style dedupe so banners can re-show.
        resetDrawStyleCache();
        bufferInitialised.current = false;
    }, [drawType]);

    // When the buffer radius changes (e.g. user edits width input in modify mode),
    // force OpenLayers to re-apply style functions on existing draw features.
    // Skip the first change (initial mount / permission default) to avoid
    // false "exceeds extent" alerts from the dedupe being reset.
    useEffect(() => {
        if (drawType !== LINE || !drawSource) {
            return;
        }
        if (!bufferInitialised.current) {
            bufferInitialised.current = true;
            return;
        }

        // Bump each feature's revision so OL invalidates cached styles,
        // then force-refresh the source itself so the Modify interaction's
        // internal overlay picks up the change (it listens to source events,
        // not individual feature revisions).
        const features = drawSource.getFeatures?.();
        if (features) {
            features.forEach(feature => feature.changed());
        }
        drawSource.changed?.();

        // During active drawing, the sketch feature lives on the Draw
        // interaction's internal overlay, not on the drawSource. Invalidate
        // that overlay so the buffer polygon re-renders immediately without
        // requiring a pointer move back into the map.
        const interactions = map?.getInteractions?.()?.getArray?.() || [];
        const drawInteraction = interactions.find(i => typeof i.finishDrawing === 'function');
        if (drawInteraction) {
            const overlay = drawInteraction.getOverlay?.();
            if (overlay) {
                const overlaySource = overlay.getSource?.();
                const overlayFeatures = overlaySource?.getFeatures?.();
                if (overlayFeatures) {
                    overlayFeatures.forEach(feature => feature.changed());
                }
                overlaySource?.changed?.();
            }
        }

        map?.renderSync?.();
    }, [bufferRadiusMetres, drawType, drawSource, map]);

    const handleDrawStart = () => {
        resetDrawStyleCache();
        addMapMode(DRAWING);
    };

    const handleDrawEnd = (evt) => {
        removeMapMode(DRAWING);
        if (mapMode.includes(DEFAULT)) {
            const feature = evt?.feature?.clone();
            if (drawSource && feature) {
                drawSource?.clear();
                aoiSource?.clear();
                aoiSource?.addFeature(feature);
                setAlertOpen(false);
            }
        }
    };

    const handleModifyStart = () => {
        resetDrawStyleCache();
        addMapMode(DRAWING);
    };

    const handleModifyEnd = () => {
        removeMapMode(DRAWING);
    };

    const handleFinishCondition = () => {
        const drawStatus = store.getState()?.draw?.status;
        return !drawStatus?.includes(INVALID) && !drawStatus?.includes(EXCEEDS_EXTENT) && !drawStatus?.includes(OUTSIDE_SERVICE_AREA);
    };

    const drawStyleFn = (feature) => {
        const currentBuffer = store.getState()?.draw?.bufferRadiusMetres;
        const buffer = drawType === LINE ? (Number.isFinite(currentBuffer) ? currentBuffer : bufferRadiusMetres) : 0;
        // Line length/extent validation: uses lineDrawDimensions.length (from AOI_DRAW_LINE permission)
        // for LINE mode, maxDrawExtent (from AOI_DRAW permission) for polygon/box modes.
        const limit = drawType === LINE ? lineDrawDimensions.length : maxDrawExtent;
        const lineWidthLimit = drawType === LINE ? lineDrawDimensions.width : undefined;
        return drawStyle(feature, limit, buffer, drawType, lineWidthLimit);
    };

    const modifyStyleFn = (feature) => {
        const currentBuffer = store.getState()?.draw?.bufferRadiusMetres;
        const buffer = drawType === LINE ? (Number.isFinite(currentBuffer) ? currentBuffer : bufferRadiusMetres) : undefined;
        const limit = drawType === LINE ? lineDrawDimensions.length : maxDrawExtent;
        const lineWidthLimit = drawType === LINE ? lineDrawDimensions.width : undefined;
        return modifyStyle(feature, limit, buffer, drawType, lineWidthLimit);
    };

    return (
        <Interactions>
            {drawSource && aoiSource
                ? <>
                    <Draw
                        active={mapMode.includes(DRAWING)}
                        style={drawStyleFn}
                        type={drawType}
                        source={drawSource}
                        onDrawStart={handleDrawStart}
                        onDrawEnd={handleDrawEnd}
                        finishCondition={handleFinishCondition}
                    />
                    <Modify
                        active={mapMode.includes(MODIFYING)}
                        style={modifyStyleFn}
                        source={drawSource}
                        onModifyStart={handleModifyStart}
                        onModifyEnd={handleModifyEnd}
                        pixelTolerance={10}
                    />
                </>
                : null
            }
        </Interactions>

    );
};

DrawAndModify.propTypes = {
    mapMode: PropTypes.array,
    drawType: PropTypes.any,
    addMapMode: PropTypes.func,
    removeMapMode: PropTypes.func,
    removeMod: PropTypes.func,
    setAlertOpen: PropTypes.func,
    clearDrawStatus: PropTypes.func
};

const mapStateToProps = state => {
    const { map, draw } = state;
    return {
        mapMode: map?.mode,
        drawType: draw?.type || BOX
    };
};

const mapDispatchToProps = dispatch => {
    return {
        addMapMode: (payload) => dispatch(addMapMode(payload)),
        removeMapMode: (payload) => dispatch(removeMapMode(payload)),
        setAlertOpen: (payload) => dispatch(setAlertOpen(payload)),
        clearDrawStatus: (payload) => dispatch(clearDrawStatus(payload))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DrawAndModify);
